'use client';

import {
  EXTERNAL_SCOPE_QUERIES,
  ExternalScopeManagementContent,
  useExternalScopeListQuery,
  useExternalScopeManagementState,
} from '@terra/ui-design-system';
import { reportTelemetryError } from '@terra/ui-telemetry';
import { useActiveExternalNetworkScan } from '../../../../hooks/api/useExternalNetworkScans';
import {
  useAddExternalNetworkScopeDomains,
  useAddExternalNetworkScopeNetblocks,
  useExternalNetworkScopeDomains,
  useExternalNetworkScopeNetblocks,
  useUpdateExternalNetworkDomainScope,
  useUpdateExternalNetworkNetblockScope,
} from '../../../../hooks/api/useExternalNetworkScope';
import { useExternalNetworkSurfaceDetail } from '../../../../hooks/api/useSurfaceSettings';
import { usePermissions } from '../../../../hooks/usePermissions';

export function ExternalAssetsSettingsPanel({
  surfaceId,
}: {
  surfaceId: string;
}) {
  const { data: surface } = useExternalNetworkSurfaceDetail(surfaceId);
  const { activeScan } = useActiveExternalNetworkScan({ surfaceId });
  const { canManageApplications } = usePermissions();
  const listQuery = useExternalScopeListQuery();
  const { netblocks, isLoading: netblocksLoading } =
    useExternalNetworkScopeNetblocks({
      surfaceId,
      query: listQuery.netblockListQuery,
    });
  const {
    domains,
    total: domainTreeTotal,
    isLoading: domainsLoading,
  } = useExternalNetworkScopeDomains({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.domainTree,
  });
  const { total: netblocksAllTotal } = useExternalNetworkScopeNetblocks({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.count,
  });
  const { total: domainsAllTotal } = useExternalNetworkScopeDomains({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.count,
  });
  const { total: netblocksPendingCount } = useExternalNetworkScopeNetblocks({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.pendingCount,
  });
  const { total: netblocksInScopeCount } = useExternalNetworkScopeNetblocks({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.inScopeCount,
  });
  const { total: netblocksOutOfScopeCount } = useExternalNetworkScopeNetblocks({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.outOfScopeCount,
  });
  const { total: domainsPendingCount } = useExternalNetworkScopeDomains({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.pendingCount,
  });
  const { total: domainsInScopeCount } = useExternalNetworkScopeDomains({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.inScopeCount,
  });
  const { total: domainsOutOfScopeCount } = useExternalNetworkScopeDomains({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.outOfScopeCount,
  });
  const { domains: userProvidedDomains } = useExternalNetworkScopeDomains({
    surfaceId,
    query: EXTERNAL_SCOPE_QUERIES.userProvided,
  });
  const { netblocks: userProvidedNetblocks } = useExternalNetworkScopeNetblocks(
    {
      surfaceId,
      query: EXTERNAL_SCOPE_QUERIES.userProvided,
    },
  );
  const updateNetblockScope = useUpdateExternalNetworkNetblockScope();
  const updateDomainScope = useUpdateExternalNetworkDomainScope();
  const addDomains = useAddExternalNetworkScopeDomains();
  const addNetblocks = useAddExternalNetworkScopeNetblocks();
  const scopeState = useExternalScopeManagementState({
    surfaceId,
    listQuery,
    netblocksPage: netblocks,
    isNetblocksPageLoading: netblocksLoading,
    domains,
    domainTreeTotal,
    isDomainsLoading: domainsLoading,
    netblockCounts: {
      all: netblocksAllTotal,
      PENDING: netblocksPendingCount,
      IN_SCOPE: netblocksInScopeCount,
      OUT_OF_SCOPE: netblocksOutOfScopeCount,
    },
    domainCounts: {
      all: domainsAllTotal,
      PENDING: domainsPendingCount,
      IN_SCOPE: domainsInScopeCount,
      OUT_OF_SCOPE: domainsOutOfScopeCount,
    },
    userProvidedDomains,
    userProvidedNetblocks,
    isConnectorSurface: surface?.egressStrategy === 'CONNECTOR',
    isScanActive: activeScan !== undefined,
    isReadOnly: !canManageApplications,
    isScopeUpdatePending:
      updateNetblockScope.isPending || updateDomainScope.isPending,
    isDomainAddPending: addDomains.isPending,
    isNetblockAddPending: addNetblocks.isPending,
    updateNetblockScope: ({ netblockId, scope }) =>
      updateNetblockScope.mutateAsync({ surfaceId, netblockId, scope }),
    updateDomainScope: ({ domainId, scope }) =>
      updateDomainScope.mutateAsync({ surfaceId, domainId, scope }),
    addDomains: (values) =>
      addDomains.mutateAsync({ surfaceId, dto: { domains: values } }),
    addNetblocks: (values) =>
      addNetblocks.mutateAsync({ surfaceId, dto: { netblocks: values } }),
    onAddError: ({ error, operation }) =>
      reportTelemetryError(error, { operation, surfaceId }),
  });

  return <ExternalScopeManagementContent {...scopeState} />;
}
